import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import type { TagOption } from '../api/adTags';

interface HierarchicalSelectProps {
  options: TagOption[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  style?: React.CSSProperties;
}

interface DropPos {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  minWidth: number;
  maxWidth: number;
}

const buildLabelMap = (options: TagOption[]) => {
  const map = new Map<string, string>();
  for (const option of options) {
    map.set(option.value, option.label);
    for (const child of option.children || []) {
      map.set(child.value, child.label);
    }
  }
  return map;
};

const filterParents = (options: TagOption[], keyword: string) => {
  const q = keyword.trim().toLowerCase();
  if (!q) return options;
  return options.filter((option) => {
    if (option.label.toLowerCase().includes(q)) return true;
    return option.children?.some((child) => child.label.toLowerCase().includes(q));
  });
};

const collectSelectableValues = (options: TagOption[]) => {
  const values: string[] = [];
  for (const option of options) {
    if (option.children?.length) {
      values.push(...option.children.map((child) => child.value));
    } else {
      values.push(option.value);
    }
  }
  return values;
};

const collectNodeValues = (option: TagOption): string[] => {
  if (option.children?.length) return option.children.map((child) => child.value);
  return [option.value];
};

export default function HierarchicalSelect({
  options,
  value,
  onChange,
  placeholder = '请选择',
  style,
}: HierarchicalSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeParent, setActiveParent] = useState<string>(options[0]?.value || '');
  const [dropPos, setDropPos] = useState<DropPos | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const labelMap = useMemo(() => buildLabelMap(options), [options]);
  const visibleParents = useMemo(() => filterParents(options, search), [options, search]);
  const currentParent = visibleParents.find((item) => item.value === activeParent) || visibleParents[0];
  const currentChildren = currentParent?.children?.length ? currentParent.children : currentParent ? [currentParent] : [];

  const calcPos = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const dropH = 360;
    const spaceBelow = window.innerHeight - r.bottom - 8;
    const spaceAbove = r.top - 8;
    const goUp = spaceBelow < dropH && spaceAbove > spaceBelow;
    const goRight = r.left + 520 > window.innerWidth - 8;
    const maxWidth = goRight ? r.right - 8 : window.innerWidth - r.left - 8;
    setDropPos({
      minWidth: Math.max(r.width, 420),
      maxWidth: Math.max(420, maxWidth),
      ...(goUp ? { bottom: window.innerHeight - r.top + 6 } : { top: r.bottom + 6 }),
      ...(goRight ? { right: window.innerWidth - r.right } : { left: r.left }),
    });
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(t) &&
        dropdownRef.current && !dropdownRef.current.contains(t)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setDropPos(null);
      return;
    }
    calcPos();
    searchRef.current?.focus();
  }, [open, calcPos]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener('scroll', calcPos, true);
    window.addEventListener('resize', calcPos);
    return () => {
      window.removeEventListener('scroll', calcPos, true);
      window.removeEventListener('resize', calcPos);
    };
  }, [open, calcPos]);

  useEffect(() => {
    if (!options.length) return;
    if (!open) return;
    if (!activeParent) {
      setActiveParent(options[0].value);
      return;
    }
    if (!visibleParents.some((item) => item.value === activeParent) && visibleParents[0]) {
      setActiveParent(visibleParents[0].value);
    }
  }, [options, visibleParents, activeParent, open]);

  const selectedText = value.length === 0
    ? placeholder
    : value.length === 1
      ? (labelMap.get(value[0]) || value[0])
      : `已选 ${value.length} 项`;

  const toggleValue = (nextValue: string) => {
    onChange(value.includes(nextValue) ? value.filter((item) => item !== nextValue) : [...value, nextValue]);
  };

  const toggleParent = (option: TagOption) => {
    const nodeValues = collectNodeValues(option);
    const allSelected = nodeValues.every((item) => value.includes(item));
    if (allSelected) {
      onChange(value.filter((item) => !nodeValues.includes(item)));
      return;
    }
    onChange(Array.from(new Set([...value, ...nodeValues])));
  };

  const isParentFullySelected = (option: TagOption) => {
    const nodeValues = collectNodeValues(option);
    return nodeValues.length > 0 && nodeValues.every((item) => value.includes(item));
  };

  const isParentPartiallySelected = (option: TagOption) => {
    const nodeValues = collectNodeValues(option);
    const selectedCount = nodeValues.filter((item) => value.includes(item)).length;
    return selectedCount > 0 && selectedCount < nodeValues.length;
  };

  return (
    <div className="ms-wrap" ref={triggerRef} style={style}>
      <button type="button" className={`ms-trigger${open ? ' open' : ''}`} onClick={() => setOpen((prev) => !prev)}>
        <span className="ms-label">{selectedText}</span>
        <span className={`ms-suffix${value.length > 0 ? ' ms-suffix--clearable' : ''}`}>
          {value.length > 0 && (
            <svg
              className="ms-icon-clear"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              width="12"
              height="12"
              onClick={(e) => {
                e.stopPropagation();
                onChange([]);
              }}
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          )}
          <svg className="ms-icon-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12" style={{ transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      {open && dropPos && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="ms-dropdown"
          style={{
            position: 'fixed',
            top: dropPos.top,
            bottom: dropPos.bottom,
            left: dropPos.left,
            right: dropPos.right,
            minWidth: dropPos.minWidth,
            maxWidth: dropPos.maxWidth,
            padding: 0,
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索..."
              style={{ width: '100%', padding: '8px 10px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 8, outline: 'none', background: 'var(--surface)', color: 'var(--text)' }}
            />
          </div>
          <div className="ms-actions">
            <button type="button" onClick={() => onChange(collectSelectableValues(visibleParents))}>全选</button>
            <button type="button" onClick={() => onChange([])}>清除</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.15fr', minHeight: 260, maxHeight: 320 }}>
            <div style={{ borderRight: '1px solid var(--border)', overflowY: 'auto', background: 'rgba(0,0,0,.015)' }}>
              {visibleParents.map((option) => {
                const active = option.value === currentParent?.value;
                const fullySelected = isParentFullySelected(option);
                const partiallySelected = isParentPartiallySelected(option);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onMouseEnter={() => setActiveParent(option.value)}
                    onClick={() => setActiveParent(option.value)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '9px 12px',
                      border: 'none',
                      background: active ? 'rgba(59,130,246,.10)' : 'transparent',
                      color: 'var(--text)',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleParent(option);
                      }}
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: 3,
                        border: (fullySelected || partiallySelected) ? '1px solid #2563eb' : '1px solid var(--border2)',
                        background: fullySelected ? '#2563eb' : '#fff',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: partiallySelected ? '#2563eb' : '#fff',
                        fontSize: 10,
                        flexShrink: 0,
                        padding: 0,
                        cursor: 'pointer',
                      }}
                    >
                      {fullySelected ? '✓' : partiallySelected ? '−' : ''}
                    </button>
                    <span style={{ flex: 1, fontWeight: active ? 700 : 500, fontSize: 13 }}>{option.label}</span>
                    {option.children?.length ? <span style={{ color: 'var(--text3)', fontSize: 14 }}>›</span> : null}
                  </button>
                );
              })}
            </div>

            <div style={{ overflowY: 'auto', background: '#fff' }}>
              {currentChildren.map((option) => {
                const checked = value.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => toggleValue(option.value)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '9px 14px',
                      border: 'none',
                      borderBottom: '1px solid rgba(0,0,0,.025)',
                      background: checked ? 'rgba(37,99,235,.06)' : '#fff',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <span
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: 3,
                        border: checked ? '1px solid #2563eb' : '1px solid var(--border2)',
                        background: checked ? '#2563eb' : '#fff',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: 10,
                        flexShrink: 0,
                      }}
                    >
                      {checked ? '✓' : ''}
                    </span>
                    <span style={{ flex: 1, fontSize: 13 }}>{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
